import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";

import { login, checkUserExist, register } from '../utils/api';
import { useTelegram } from './TelegramContext';
import { assetBackgroundImage, assetPath } from "../utils/assets";
import { AUTH_SESSION_EXPIRED_EVENT, clearStoredAuthSession, isTelegramMiniApp } from "../utils/authSession";

const AuthContext = createContext();

const persistAuthLanguage = (languageId) => {
    if (languageId == null || languageId === "") {
        return;
    }

    localStorage.setItem('language_id', String(languageId));
    localStorage.removeItem('language');
};

const AuthOverlay = ({ loading, failed, errorMessage, reauthRequired, sessionNotice, t }) => {
    if (!loading && !failed && !reauthRequired && !sessionNotice) {
        return null;
    }

    return (
        <div
            className="fixed top-0 left-0 w-full h-full flex-center bg-black bg-opacity-75 z-100 flex-col"
            style={{
                backgroundImage: assetBackgroundImage("/icons/loading_bg.png"),
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="mb-7">
                <img
                    src={assetPath("/icons/logo.png")}
                    className="w-[200px]"
                    alt="Logo"
                />
            </div>

            {sessionNotice ? (
                <>
                    <div className="mb-7 max-w-xs px-4 text-center text-lg text-white">
                        {t("webSessionExpired")}
                    </div>
                    <button
                        onClick={() => window.location.assign("/login")}
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        {t("login")}
                    </button>
                </>
            ) : reauthRequired ? (
                <>
                    <div className="mb-7 max-w-xs px-4 text-center text-lg text-white">
                        {t("telegramSessionExpired")}
                    </div>
                    <button
                        onClick={() => {
                            if (window.Telegram?.WebApp?.close) {
                                window.Telegram.WebApp.close();
                                return;
                            }

                            window.location.reload();
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        {t("close")}
                    </button>
                </>
            ) : loading ? (
                <div className="loader"></div>
            ) : (
                <>
                    <div className="mb-7 text-white text-lg text-center">
                        {errorMessage}
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                    >
                        {t("retry")}
                    </button>
                </>
            )}

            <style>
                {`
                    .loader {
                        height: 30px;
                        aspect-ratio: 2.5;
                        --_g: no-repeat radial-gradient(farthest-side, #fff 90%, #0000);
                        background: var(--_g), var(--_g), var(--_g), var(--_g);
                        background-size: 20% 50%;
                        animation: l43 1s infinite linear;
                    }
                    @keyframes l43 {
                        0%     {background-position: calc(0*100%/3) 50%, calc(1*100%/3) 50%, calc(2*100%/3) 50%, calc(3*100%/3) 50%}
                        16.67% {background-position: calc(0*100%/3) 0, calc(1*100%/3) 50%, calc(2*100%/3) 50%, calc(3*100%/3) 50%}
                        33.33% {background-position: calc(0*100%/3) 100%, calc(1*100%/3) 0, calc(2*100%/3) 50%, calc(3*100%/3) 50%}
                        50%    {background-position: calc(0*100%/3) 50%, calc(1*100%/3) 100%, calc(2*100%/3) 0, calc(3*100%/3) 50%}
                        66.67% {background-position: calc(0*100%/3) 50%, calc(1*100%/3) 50%, calc(2*100%/3) 100%, calc(3*100%/3) 0}
                        83.33% {background-position: calc(0*100%/3) 50%, calc(1*100%/3) 50%, calc(2*100%/3) 50%, calc(3*100%/3) 100%}
                        100%   {background-position: calc(0*100%/3) 50%, calc(1*100%/3) 50%, calc(2*100%/3) 50%, calc(3*100%/3) 50%}
                    }
                `}
            </style>
        </div>
    );
};

const AuthProviderBase = ({ children, value, authStatus, errorMessage, reauthRequired = false, sessionNotice = false, t }) => (
    <AuthContext.Provider value={value}>
        <AuthOverlay
            loading={authStatus === 'loading'}
            failed={authStatus === 'failed'}
            errorMessage={errorMessage}
            reauthRequired={reauthRequired}
            sessionNotice={sessionNotice}
            t={t}
        />
        {(authStatus === 'authenticated' || authStatus === 'guest') && children}
    </AuthContext.Provider>
);

const TelegramAuthProvider = ({ children }) => {
    const { user } = useTelegram();
    const { t } = useTranslation();

    const [token, setToken] = useState(localStorage.getItem('token'));
    const [authStatus, setAuthStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem('phone_number') || '');
    const [referralCode, setReferralCode] = useState('');
    const [reauthRequired, setReauthRequired] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const phone = urlParams.get("phone");
        const ref = urlParams.get("ref");

        if (ref) setReferralCode(ref);
        if (phone) setPhoneNumber(phone);
    }, []);

    const setAuthToken = useCallback((newToken, nextPhoneNumber = phoneNumber, nextLanguageId = null) => {
        setToken(newToken);
        setReauthRequired(false);
        localStorage.setItem('token', newToken);
        persistAuthLanguage(nextLanguageId);
        if (nextPhoneNumber) {
            setPhoneNumber(nextPhoneNumber);
            localStorage.setItem('phone_number', nextPhoneNumber);
        }
    }, [phoneNumber]);

    const logout = useCallback(() => {
        clearStoredAuthSession("logout");
        setToken(null);
        setPhoneNumber('');
        setErrorMessage('');
        setReauthRequired(false);
        setAuthStatus('guest');
    }, []);

    useEffect(() => {
        const expireTelegramSession = () => {
            setToken(null);
            setPhoneNumber('');
            setErrorMessage('');
            setReauthRequired(true);
            setAuthStatus('guest');
        };

        const handleSessionExpired = () => {
            expireTelegramSession();
        };

        const handleStorage = (event) => {
            if (event.key === 'token' && event.oldValue && !event.newValue) {
                expireTelegramSession();
            }
        };

        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const autoLogin = async () => {
            let telegramUser = user;

            if (reauthRequired || !phoneNumber || !telegramUser || !isMounted) return;

            try {
                const userCheckResponse = await checkUserExist({ password: telegramUser.id });
                if (userCheckResponse.success) {
                    const loginResponse = await login({ phoneNumber: userCheckResponse.data, password: telegramUser.id, telegram_id: telegramUser.id });

                    if (loginResponse.success && loginResponse.data) {
                        setAuthToken(loginResponse.data, userCheckResponse.data, loginResponse.language);
                        setAuthStatus('authenticated');
                    } else {
                        setErrorMessage(loginResponse.data);
                        setAuthStatus('failed');
                    }
                } else {
                    const registerResponse = await register({
                        phone_number: phoneNumber,
                        telegram_id: telegramUser.id,
                        language: telegramUser.language_code,
                        referral_code: referralCode || ''
                    });

                    if (registerResponse.success) {
                        const loginResponse = await login({ phoneNumber: registerResponse.data, password: telegramUser.id, telegram_id: telegramUser.id });

                        if (loginResponse.success && loginResponse.data) {
                            setAuthToken(loginResponse.data, registerResponse.data, loginResponse.language);
                            setAuthStatus('authenticated');
                        } else {
                            setErrorMessage(loginResponse.data);
                            setAuthStatus('failed');
                        }
                    } else {
                        setErrorMessage(registerResponse.data);
                        setAuthStatus('failed');
                    }
                }
            } catch (error) {
                setErrorMessage(t("networkErrorPleaseTryAgain"));
                setAuthStatus('failed');
            }
        };

        autoLogin();

        return () => {
            isMounted = false;
        };
    }, [phoneNumber, reauthRequired, referralCode, setAuthToken, user]);

    const value = useMemo(() => ({
        token,
        setAuthToken,
        logout,
        authStatus,
        phoneNumber,
        referralCode,
        user
    }), [token, setAuthToken, logout, authStatus, phoneNumber, referralCode, user]);

    return (
        <AuthProviderBase
            value={value}
            authStatus={authStatus}
            errorMessage={errorMessage}
            reauthRequired={reauthRequired}
            t={t}
        >
            {children}
        </AuthProviderBase>
    );
};

const WebAuthProvider = ({ children }) => {
    const { t } = useTranslation();
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem('phone_number') || '');
    const [authStatus, setAuthStatus] = useState(() => (localStorage.getItem('token') ? 'authenticated' : 'guest'));
    const [sessionNotice, setSessionNotice] = useState(false);

    useEffect(() => {
        setAuthStatus(token ? 'authenticated' : 'guest');
    }, [token]);

    const setAuthToken = useCallback((newToken, nextPhoneNumber = '', nextLanguageId = null) => {
        setToken(newToken);
        setSessionNotice(false);
        localStorage.setItem('token', newToken);
        persistAuthLanguage(nextLanguageId);
        if (nextPhoneNumber) {
            setPhoneNumber(nextPhoneNumber);
            localStorage.setItem('phone_number', nextPhoneNumber);
        }
        setAuthStatus('authenticated');
    }, []);

    const logout = useCallback(() => {
        clearStoredAuthSession("logout");
        setToken(null);
        setPhoneNumber('');
        setSessionNotice(false);
        setAuthStatus('guest');
    }, []);

    useEffect(() => {
        const expireWebSession = (showNotice = false) => {
            setToken(null);
            setPhoneNumber('');
            setSessionNotice(showNotice);
            setAuthStatus('guest');
        };

        const handleSessionExpired = (event) => {
            expireWebSession(event?.detail?.reason !== "logout");
        };

        const handleStorage = (event) => {
            if (event.key === 'token' && event.oldValue && !event.newValue) {
                expireWebSession(true);
            }
        };

        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    const value = useMemo(() => ({
        token,
        setAuthToken,
        logout,
        authStatus,
        phoneNumber,
        referralCode: '',
        user: null
    }), [token, setAuthToken, logout, authStatus, phoneNumber]);

    return (
        <AuthProviderBase
            value={value}
            authStatus={authStatus}
            errorMessage=""
            sessionNotice={sessionNotice}
            t={t}
        >
            {children}
        </AuthProviderBase>
    );
};

export const AuthProvider = ({ children }) => {
    if (isTelegramMiniApp()) {
        return <TelegramAuthProvider>{children}</TelegramAuthProvider>;
    }

    return <WebAuthProvider>{children}</WebAuthProvider>;
};

export const getCurrentToken = () => localStorage.getItem('token');
export const useAuth = () => useContext(AuthContext);